// CompanyTable.jsx
import React, { useMemo } from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { IconButton, Tooltip, Box, Chip, Switch, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR");
}

const CompanyTable = ({
  companies = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onEditCompany,
  onDeleteCompany,
  onDeleteSelected,
  onToggleStatus,
  isLoading,
  bulkActions,
}) => {
  const { t } = useTranslation("settings");
  const rows = useMemo(
    () =>
      companies.map((c) => ({
        id: c.id,
        nome: c.nome || "—",
        slug: c.codigo_interno || "—",
        statusRaw: Number(c.status) === 1 ? 1 : 0,
        // pega sempre a última alteração; se não houver, usa data de cadastro
        updatedAt: c.data_ultima_alteracao || c.data_cadastro || null,
      })),
    [companies]
  );

  const columns = [
    {
      field: "nome",
      headerName: t("companyTable.colCompany"),
      width: 420,
      renderCell: (p) => (
        <Stack spacing={0.3} sx={{ height: "100%", justifyContent: "center" }}>
          <Typography fontWeight={600} noWrap>
            {p.row.nome}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {p.row.slug !== "—" ? `/${p.row.slug}` : "—"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "updatedAt",
      headerName: t("companyTable.colLastUpdate"),
      width: 220,
      renderCell: (p) => (
        <Box sx={{ height: "100%", display: "flex", alignItems: "center" }}>
          <Typography variant="body2" noWrap>
            {fmtDateTime(p.row.updatedAt)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: t("shared.status"),
      width: 180,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ height: "100%" }}>
          <Switch
            size="small"
            checked={Number(params.row.statusRaw) === 1}
            onChange={() => onToggleStatus?.(params.row)}
          />
          <Chip
            size="small"
            label={Number(params.row.statusRaw) === 1 ? t("shared.active") : t("shared.inactive")}
            color={Number(params.row.statusRaw) === 1 ? "success" : "default"}
            variant="outlined"
          />
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: t("shared.actions"),
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%" }}>
          <Tooltip title={t("shared.edit")}>
            <IconButton size="small" onClick={() => onEditCompany?.(params.row.id)}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("shared.delete")}>
            <IconButton size="small" onClick={() => onDeleteCompany?.(params.row.id)}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle={t("companyTable.exportTitle")}
      exportFileName="empresas"
      onDeleteSelected={onDeleteSelected}
      bulkActions={bulkActions}
      searchPlaceholder={t("companyTable.searchPlaceholder")}
      pageSizeOptions={[10, 25, 50, 100]}
      dense
    />
  );
};

export default CompanyTable;
